import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { searchSite, searchIndex, SearchEntry } from "@/data/searchIndex";
import { useI18n } from "@/i18n";
import { cn } from "@/lib/utils";

interface SiteSearchProps {
  className?: string;
  iconClassName?: string;
}

const GROUP_ORDER: SearchEntry["group"][] = ["Products", "Bath & Sanitary", "Pages", "Blog"];

export const SiteSearch = ({ className, iconClassName }: SiteSearchProps) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const navigate = useNavigate();
  const { getLocalizedPath } = useI18n();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const results = useMemo(
    () => (query.trim() ? searchSite(query) : searchIndex.filter((e) => e.group === "Products").slice(0, 8)),
    [query]
  );

  const grouped = useMemo(
    () =>
      GROUP_ORDER.map((group) => ({
        group,
        items: results.filter((r) => r.group === group),
      })).filter((g) => g.items.length > 0),
    [results]
  );

  const go = (path: string) => {
    setOpen(false);
    setQuery("");
    navigate(getLocalizedPath(path));
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Search the site"
        className={cn("p-2 transition-colors hover:text-accent", className)}
      >
        <Search className={cn("w-5 h-5", iconClassName)} />
      </button>

      <CommandDialog open={open} onOpenChange={setOpen} shouldFilter={false}>
        <CommandInput
          placeholder="Search products, pages and articles..."
          value={query}
          onValueChange={setQuery}
        />
        <CommandList>
          <CommandEmpty>No results found.</CommandEmpty>
          {grouped.map(({ group, items }) => (
            <CommandGroup key={group} heading={group}>
              {items.map((item) => (
                <CommandItem key={item.path} value={`${item.title} ${item.path}`} onSelect={() => go(item.path)}>
                  <div className="flex flex-col">
                    <span className="font-medium">{item.title}</span>
                    {item.description && (
                      <span className="text-xs text-muted-foreground line-clamp-1">{item.description}</span>
                    )}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          ))}
        </CommandList>
      </CommandDialog>
    </>
  );
};

export default SiteSearch;
